import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigationType } from 'react-router-dom';
import { haySesionActiva } from '../config/api';
import useRutaConsola from '../hooks/useRutaConsola';
import { leerRuta, rutaSelector } from '../rutas/rutasConsola';

const esRutaDeConsola = (pathname) => leerRuta(pathname) != null;

/**
 * El atrás del móvil, con sesión, no debe irse a Google ni al login
 * sin avisar. Este listener vive en el router, no en el Dashboard:
 * si estuviera ahí, el cambio de ruta a `/` lo desmontaría.
 *
 * El componente se monta en `/` (login) y no se remonta al entrar
 * al panel: la trampa se arma una vez por sesión, en la primera ruta
 * de la consola. El Dashboard avisa con `dashboard-quiere-salir` cuando
 * el atrás no cierra un chat o sección. El logout borra el marcador de
 * sesión y navega a `/` a propósito; ese camino no es un popstate.
 *
 * Mientras el PR 3 de rutas no retire el historial manual del Dashboard,
 * las redirecciones automáticas de la consola sustituyen la entrada de
 * la trampa: aquí se vuelve a marcar, y si el atrás cae en la entrada de
 * debajo (con la URL de entrada, p. ej. `/bots`), se vuelve a la de la
 * trampa para que el aviso salga sobre la misma pantalla.
 */
function ImpedirSalidaAlLogin() {
  // El navigate estable: así el listener de popstate se registra una sola
  // vez, antes que el del Dashboard, y corre siempre primero.
  const { navigate } = useRutaConsola();
  const navigationType = useNavigationType();
  const { pathname } = useLocation();
  const [aviso, setAviso] = useState(false);
  const avisoRef = useRef(false);
  const saliendoRef = useRef(false);
  const armadaRef = useRef(false);
  const enTrampaRef = useRef(false);
  const rutaTrampaRef = useRef(null);

  const marcarEnTrampa = () => {
    enTrampaRef.current = true;
    rutaTrampaRef.current = window.location.pathname;
  };

  const armarTrampa = () => {
    if (!haySesionActiva()) return;
    if (!esRutaDeConsola(window.location.pathname)) return;
    if (window.history.state?.salida !== 'trampa') {
      window.history.pushState(
        { view: 'dashboard', salida: 'trampa' },
        '',
        window.location.pathname + window.location.search,
      );
    }
    marcarEnTrampa();
  };

  const mostrarAviso = () => {
    avisoRef.current = true;
    setAviso(true);
  };

  useEffect(() => {
    if (pathname === '/') {
      armadaRef.current = false;
      enTrampaRef.current = false;
      return;
    }
    if (!esRutaDeConsola(pathname)) return;
    if (!armadaRef.current) {
      armadaRef.current = true;
      armarTrampa();
      return;
    }
    if (navigationType === 'PUSH') {
      enTrampaRef.current = false;
    } else if (navigationType === 'REPLACE' && enTrampaRef.current) {
      if (window.history.state?.salida !== 'trampa') {
        window.history.replaceState({ ...window.history.state, salida: 'trampa' }, '');
      }
      marcarEnTrampa();
    }
  }, [pathname, navigationType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onQuiereSalir = () => {
      if (saliendoRef.current) return;
      mostrarAviso();
    };

    const onPopState = () => {
      if (saliendoRef.current) return;
      if (!haySesionActiva()) return;

      if (window.location.pathname === '/') {
        navigate(rutaTrampaRef.current || rutaSelector(), { replace: true });
        mostrarAviso();
        return;
      }

      const veniaDeLaTrampa = enTrampaRef.current;
      enTrampaRef.current = window.history.state?.salida === 'trampa';
      if (veniaDeLaTrampa && !enTrampaRef.current
          && rutaTrampaRef.current && window.location.pathname !== rutaTrampaRef.current) {
        navigate(rutaTrampaRef.current, { replace: true });
      }

      queueMicrotask(() => {
        if (window.history.state?.salida === 'trampa') marcarEnTrampa();
        if (avisoRef.current || saliendoRef.current) return;
        armarTrampa();
      });
    };

    window.addEventListener('dashboard-quiere-salir', onQuiereSalir);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('dashboard-quiere-salir', onQuiereSalir);
      window.removeEventListener('popstate', onPopState);
    };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const quedarse = () => {
    saliendoRef.current = false;
    avisoRef.current = false;
    setAviso(false);
    armarTrampa();
  };

  const salir = () => {
    saliendoRef.current = true;
    avisoRef.current = false;
    setAviso(false);
    window.history.back();
  };

  if (!aviso) return null;

  return createPortal(
    <div className="modal-overlay" onClick={quedarse}>
      <div
        className="modal-content confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salida-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="salida-title">Salir</h3>
        </div>
        <div className="confirm-modal-body">
          <p>¿Seguro que quieres salir?</p>
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="admin-btn" onClick={quedarse}>
            Mejor no
          </button>
          <button type="button" className="admin-btn danger" onClick={salir}>
            Salir
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ImpedirSalidaAlLogin;
